import {
  Model,
  PageModel,
  PanelModel,
  SurveyModel,
  Serializer,
  ChoicesLazyLoadEvent,
  UpdateQuestionCssClassesEvent,
  ITheme,
  GetChoiceDisplayValueEvent,
} from "survey-core";

// Register the moreInfo property and its mode
Serializer.addProperty("question", {
  name: "moreInfo:string",
  default: "",
});

Serializer.addProperty("question", {
  name: "moreInfoMode",
  type: "text",
  default: "inline",
});

interface ISurveyProps {
  title?: string;
  description?: string;
  showQuestionNumbers?: boolean;
  showPrevButton?: boolean;
  showNextButton?: boolean;
  showCompleteButton?: boolean;
  completeText?: string;
  pageNextText?: string;
  pagePrevText?: string;
  startSurveyText?: string;
  showProgressBar?: boolean;
  progressBarLocation?: string;
  progressBarShowPageTitles?: boolean;
  progressBarShowPageNumbers?: boolean;
  showTOC?: boolean;
  tocLocation?: string;
  headerView?: string;
  showNavigationButtons?: boolean;
  wrapperFormCssClass?: string;
  showPreviewBeforeComplete?: boolean;
  questionsOnPageMode?: "standard" | "singlePage" | "questionPerPage";
  [key: string]: any;
}

export interface IProps {
  onComplete: (data: any) => void;
  initialData?: any;
  surveyFormId: string;
  pageSchemas: any[];
  surveyProps?: ISurveyProps;
  onPageNext?: (data: any) => void;
  onPagePrev?: (data: any) => void;
  onBeforeComplete?: (data: any) => boolean | void;
  surveyTheme?: ITheme;
  wrapperFormCssClass?: string;
  onPageChanging?: (
    newCurrentPage: PageModel,
    oldCurrentPage: PageModel
  ) => void;
  autoskip?: boolean;
  autoAdvanceEnabled?: boolean;
  addCSSClassToQuestions?: IAddCSSClassToQuestion[];
  testModel?: Model;
  showSaveAndExit?: boolean;
  onSaveAndExit?: (data: any) => void;
  keepFormVisibleOnComplete?: boolean;
  suppressLoader?: boolean;
  disableCompleteUntilCurrentPageComplete?: boolean;
  surveyErrorTextColor?: string;
  /** Fires once per page, on that page's first value change (not on mount/page-transition) - for funnel "start" events that should reflect actual engagement rather than just the page rendering. Called with the current page's schema name. */
  onFirstInteraction?: (pageName: string) => void;
  /** Set false for a step in a multi-step journey whose answers must stay in the draft after the step is submitted. */
  clearDraftOnComplete?: boolean;
  /** Skips the `?tk=` resubmission prefill, for forms that must not be filled from the token (or that prefill it themselves). */
  disableTokenPrefill?: boolean;
}

export interface IAddCSSClassToQuestion {
  questionName: string;
  cssClass: string;
}

export interface ILazyLoadingDropdownField {
  name: string;
  choicesURL: string;
  displayValuesURL: string;
  takeParameterName: string;
  choicesFilterParameterName: string;
  displayValuesFilterParameterName: string;
  categoryParameterName?: string;
  categoryFieldName?: string;
}

interface ILazyLoadingDropdownFields {
  LazyLoadingDropdownFields: ILazyLoadingDropdownField[];
}

export const unflattenData = (data: { [key: string]: any }) => {
  const result: { [key: string]: any } = {};

  Object.keys(data).forEach((key) => {
    if (key.includes(".") || key.includes("[")) {
      const parts = key.split(/[.\[\]]/).filter(Boolean);
      let current = result;

      for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        const nextPart = parts[i + 1];
        if (!isNaN(Number(nextPart))) {
          if (!current[part]) {
            current[part] = [];
          }
          while (current[part].length <= Number(nextPart)) {
            current[part].push({});
          }
          current = current[part][Number(nextPart)];
        } else {
          if (!current[part]) {
            current[part] = {};
          }
          current = current[part];
        }
      }

      current[parts[parts.length - 1]] = data[key];
    } else {
      result[key] = data[key];
    }
  });

  return result;
};

export const handleQuestionRendering = (sender: any, options: any) => {
  const question = options.question;
  const htmlElement = options.htmlElement;

  if (
    question.getType?.() === "nouislider" &&
    (Array.isArray(question.tooltipTexts) || question.tooltipSuffix)
  ) {
    const syncNoUiSliderTooltip = () => {
      const rawSliderValue =
        question.value ??
        (question.noUiSlider && typeof question.noUiSlider.get === "function"
          ? question.noUiSlider.get()
          : 0);
      const sliderIndex = Number(rawSliderValue ?? 0);
      const mappedTooltipText = Array.isArray(question.tooltipTexts)
        ? question.tooltipTexts[sliderIndex]
        : undefined;
      const fallbackTooltipText = `${sliderIndex}${question.tooltipSuffix ?? ""}`;
      const tooltipText = mappedTooltipText ?? fallbackTooltipText;

      htmlElement.querySelectorAll(".noUi-tooltip").forEach((tooltip: Element) => {
        tooltip.textContent = tooltipText;
      });
    };

    if (!(question as any).__tooltipLabelBound && question.noUiSlider) {
      question.noUiSlider.on("update.tooltipLabel", syncNoUiSliderTooltip);
      (question as any).__tooltipLabelBound = true;
    }

    setTimeout(syncNoUiSliderTooltip, 0);
  }

  // Handle moreInfo button if set
  if (question.moreInfo) {
    const titleElement = htmlElement.querySelector(".sd-title");
    if (!titleElement) return;

    const mode: string = question.moreInfoMode || "inline";

    // Avoid duplicating the button if re-rendered
    if (titleElement.querySelector(".custom-more-info-btn-container")) {
      return;
    }

    // Create more info button container
    const buttonContainer = document.createElement("div");
    buttonContainer.className =
      "custom-more-info-btn-container d-inline-flex align-items-center ms-2";

    // Create more info button
    const moreInfoButton = document.createElement("button");
    moreInfoButton.className =
      "badge badge-info badge-pill text-white border-0";
    moreInfoButton.setAttribute("type", "button");
    moreInfoButton.innerHTML = "More info";

    // Attach button to container
    buttonContainer.appendChild(moreInfoButton);

    // Insert near required asterisk if present
    const requiredAsterisk = titleElement.querySelector(".sd-required");
    if (requiredAsterisk) {
      requiredAsterisk.after(buttonContainer);
    } else {
      titleElement.appendChild(buttonContainer);
    }

    // ----- INLINE MODE (existing behaviour, just tidied) -----
    if (mode === "inline") {
      // Check if we already created the box
      let moreInfoBox = htmlElement.querySelector(
        ".custom-more-info-box"
      ) as HTMLDivElement | null;

      if (!moreInfoBox) {
        moreInfoBox = document.createElement("div");
        moreInfoBox.className =
          "custom-more-info-box alert alert-info border-dotted mt-2 p-3 text-wrap position-relative";
        moreInfoBox.style.cssText =
          "display: none; position: absolute; z-index: 1000; width: 100%;";

        // Create a paragraph for the more info text
        const moreInfoText = document.createElement("p");
        moreInfoText.className = "mb-0";
        moreInfoText.innerHTML = question.moreInfo;

        // Add the text to the more info box
        moreInfoBox.appendChild(moreInfoText);

        // Insert the more info box after the title element
        if (titleElement.parentNode) {
          titleElement.parentNode.insertBefore(
            moreInfoBox,
            titleElement.nextSibling
          );
        }
      }

      // Click handler to toggle visibility
      moreInfoButton.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!moreInfoBox) return;
        const isHidden =
          moreInfoBox.style.display === "none" || !moreInfoBox.style.display;
        moreInfoBox.style.display = isHidden ? "block" : "none";
      };

      return;
    }

    // ----- TRAY MODE (slides in from the right, responsive) -----
    if (mode === "tray") {
      // Check if a tray for this question already exists
      const existingTray = document.querySelector(
        `.custom-more-info-tray[data-question-name="${question.name}"]`
      ) as HTMLDivElement | null;

      const tray = existingTray || document.createElement("div");
      tray.className = "custom-more-info-tray";
      tray.setAttribute("data-question-name", question.name);

      if (!existingTray) {
        Object.assign(tray.style, trayStyles.tray);
      }

      // Clear previous content (if re-rendered)
      tray.innerHTML = "";

      // Close button
      const closeButton = document.createElement("button");
      closeButton.className = "custom-more-info-tray-close";
      closeButton.setAttribute("type", "button");
      closeButton.setAttribute("aria-label", "Close");
      closeButton.innerHTML = "×";
      Object.assign(closeButton.style, trayStyles.closeButton);

      // Content wrapper
      const content = document.createElement("div");
      content.className = "custom-more-info-tray-content";

      if (typeof question.moreInfo === "string") {
        content.innerHTML = createWaistTooltipContent().innerHTML;
      } else {
        const p = document.createElement("p");
        p.textContent = String(question.moreInfo);
        content.appendChild(p);
      }

      // Light formatting for common tags
      const firstH4 = content.querySelector("h4") as HTMLElement | null;
      if (firstH4) {
        Object.assign(firstH4.style, trayStyles.header);
      }
      content.querySelectorAll("h5").forEach((h5) => {
        Object.assign((h5 as HTMLElement).style, trayStyles.sectionTitle);
      });
      content.querySelectorAll("ul").forEach((ul) => {
        Object.assign((ul as HTMLElement).style, trayStyles.list);
      });

      tray.appendChild(closeButton);
      tray.appendChild(content);

      if (!existingTray) {
        document.body.appendChild(tray);
      }

      const openTray = () => {
        applyResponsiveTrayWidth(tray);
        tray.style.transform = "translateX(0)";
      };

      const closeTray = () => {
        tray.style.transform = "translateX(100%)";
      };

      closeButton.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        closeTray();
      };

      // Toggle via the "More info" button
      moreInfoButton.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const isClosed =
          tray.style.transform === "" || tray.style.transform.includes("100%");
        if (isClosed) {
          openTray();
        } else {
          closeTray();
        }
      };
    }
  }
};

export function sendRequest(
  url: string,
  onloadSuccessCallback: (data: any) => void
) {
  const xhr = new XMLHttpRequest();
  xhr.open("GET", url);
  xhr.setRequestHeader("Content-Type", "application/json");
  xhr.onload = () => {
    if (xhr.status === 200) {
      try {
        const json = JSON.parse(xhr.response);
        onloadSuccessCallback(json);
      } catch (e) {
        console.error("Invalid JSON:", e);
      }
    } else {
      console.error("Request failed:", xhr.status, xhr.responseText);
    }
  };
  xhr.onerror = () => {
    console.error("Network error during request to", url);
  };
  xhr.send();
}

export const onChoicesLazyLoadFunction = (
  _: Model,
  options: ChoicesLazyLoadEvent,
  lazyLoadingDropdownFields: ILazyLoadingDropdownField[]
) => {
  const field = lazyLoadingDropdownFields.find(
    (field) => field.name === options.question.name
  );

  if (field) {
    let url = "";

    if (field.categoryFieldName) {
      let categoryFieldValue = "";
      const panel = options.question.parent as PanelModel;
      if (panel && typeof panel.getQuestionByName === "function") {
        const categoryField = panel.getQuestionByName(field.categoryFieldName);
        categoryFieldValue = categoryField.value;
      } else {
        // If not in a dynamic panel, fall back to setting values directly on the survey
        const survey = options.question.survey as SurveyModel;
        if (survey) {
          categoryFieldValue = survey.getValue(field.categoryFieldName);
        }
      }

      url = `${field.choicesURL}?${
        field.categoryParameterName
      }=${categoryFieldValue}&${
        field.choicesFilterParameterName
      }=${encodeURIComponent(options.filter || "")}&${
        field.takeParameterName
      }=${options.take}`;
    } else {
      url = `${field.choicesURL}?${
        field.choicesFilterParameterName
      }=${encodeURIComponent(options.filter || "")}&${
        field.takeParameterName
      }=${options.take}`;
    }

    sendRequest(url, (data: any[]) => {
      const formattedItems = data.map((item) => ({
        value: item.id,
        text: item.name,
      }));

      // Provide both items and totalCount (use length if backend doesn't support total count)
      options.setItems(
        formattedItems as Array<{ value: string; text: string }>,
        data.length
      );
    });
  }
};

export const onGetChoiceDisplayValueFunction = (
  _: Model,
  options: GetChoiceDisplayValueEvent,
  lazyLoadingDropdownFields: ILazyLoadingDropdownField[]
) => {
  const field = lazyLoadingDropdownFields.find(
    (field) => field.name === options.question.name
  );

  if (field) {
    if (!options.values || options.values.length === 0) return;

    const filterParam = options.values
      .map((v) => encodeURIComponent(v))
      .join(",");

    //if category is not null, add it to the url
    let url = "";
    if (field.categoryFieldName) {
      let categoryFieldValue = "";
      const panel = options.question.parent as PanelModel;
      if (panel && typeof panel.getQuestionByName === "function") {
        const categoryField = panel.getQuestionByName(field.categoryFieldName);
        categoryFieldValue = categoryField.value;
      } else {
        // If not in a dynamic panel, fall back to setting values directly on the survey
        const survey = options.question.survey as SurveyModel;
        if (survey) {
          categoryFieldValue = survey.getValue(field.categoryFieldName);
        }
      }
      url = `${field.displayValuesURL}?${field.categoryParameterName}=${categoryFieldValue}&${field.displayValuesFilterParameterName}=${filterParam}`;
    } else {
      url = `${field.displayValuesURL}?${field.displayValuesFilterParameterName}=${filterParam}`;
    }

    sendRequest(url, (data: any[]) => {
      const labels = data.map((item) => item);
      options.setItems(labels);
    });
  }
};

export const onUpdateQuestionCssClassesFunction = (
  _: Model,
  options: UpdateQuestionCssClassesEvent
) => {
  const classes = options.cssClasses;
  const elementCustomClass =
    options.question.getPropertyValue("elementCssClass");

  if (elementCustomClass) {
    classes.mainRoot += ` ${elementCustomClass}`;
  }
};

export const updateDataFromUrlParams = (surveyData: any) => {
  const urlParams = new URLSearchParams(window.location.search);

  if (urlParams) {
    // Check each URL parameter
    urlParams.forEach((value, key) => {
      if (key.includes(".") || key.includes("[")) {
        // Handle nested properties and array indices
        setNestedValue(surveyData, key, value);
      } else {
        // Handle top-level properties
        if (surveyData[key] === undefined || surveyData[key] === "") {
          surveyData[key] = value;
        }
      }
    });
  }
  return surveyData;
};


export const getPrefillDataFromTokenHelper = async (
  getPrefillDataFromTokenFn: ((token: string) => Promise<any>) | undefined
): Promise<{ surveyData: any; updatedAt: string } | undefined> => {
  

  if (!getPrefillDataFromTokenFn) return undefined;
  const urlToken = new URLSearchParams(window.location.search).get("tk");
  if (!urlToken?.trim()) return undefined;

  const normalizeDate = (isoStr?: string) => {
    return isoStr ? isoStr.split("T")[0] : undefined;
  };

  try {
    const data = await getPrefillDataFromTokenFn(urlToken);
    
    // 3. Validation: Stop if no data
    if (!data || Object.keys(data).length === 0) return undefined;

    const rawMap = {
      title: data.title,
      lastName: data.lastName,
      email: data.email,
      postcode: data.postcode,
      dateOfBirth: normalizeDate(data.dateOfBirth),
      primaryPhone: data.mobilePhone,      
      transferToID: data.leadID,           
      tk: data.token,                     
      sessionID: data.sessionID,
      
      // Handle nested Annuity object if relevant fields exist
      annuity: (data.annuityTotalFundAmount || data.retirementDate) ? {
        pensionFund: data.annuityTotalFundAmount ? String(data.annuityTotalFundAmount) : undefined,
        plannedRetirementDate: normalizeDate(data.retirementDate),
      } : undefined
    };

    return {
      surveyData: JSON.parse(JSON.stringify(rawMap)),
      updatedAt: new Date().toISOString()
    };

  } catch (error) {
    // console.error("Error fetching prefill data:", error);
    return undefined;
  }
};

const setNestedValue = (obj: any, path: string, value: any) => {
  const keys = path.split(/[.\[\]]/).filter(Boolean);
  let current = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    const nextKey = keys[i + 1];

    // Handle array indices
    if (!isNaN(Number(nextKey))) {
      if (!current[key]) {
        current[key] = [];
      }
      while (current[key].length <= Number(nextKey)) {
        current[key].push({});
      }
      current = current[key][Number(nextKey)];
    } else {
      if (!current[key]) {
        current[key] = {};
      }
      current = current[key];
    }
  }

  const lastKey = keys[keys.length - 1];
  // Only set if the value is not already set
  if (current[lastKey] === undefined || current[lastKey] === "") {
    current[lastKey] = value;
  }
};

export const getFlattenedData = (surveyData: any) => {
  if (surveyData) {
    const flattenedData: { [key: string]: any } = {};
    Object.keys(surveyData).forEach((key) => {
      let initialDataProp = surveyData[key];

      // Object
      if (
        initialDataProp !== null &&
        !Array.isArray(initialDataProp) &&
        typeof initialDataProp === "object"
      ) {
        // Array inside the object
        if (Array.isArray(initialDataProp)) {
          initialDataProp.forEach((item: any, index: number) => {
            Object.keys(item).forEach((subKey) => {
              let value = item[subKey];
              if (
                typeof value === "string" &&
                /^\d{2}-\d{2}-\d{4}$/.test(value)
              ) {
                const [day, month, year] = value.split("-");
                value = `${year}-${month}-${day}`;
              }

              flattenedData[`${key}[${index}].${subKey}`] = value;
            });
          });
        }
        // Property inside the object
        else {
          Object.keys(initialDataProp).forEach((subKey) => {
            let value = initialDataProp[subKey];
            if (
              typeof value === "string" &&
              /^\d{2}-\d{2}-\d{4}$/.test(value)
            ) {
              const [day, month, year] = value.split("-");
              value = `${year}-${month}-${day}`;
            }

            flattenedData[`${key}.${subKey}`] = value;
          });
        }
      }
      // Top level array
      else if (Array.isArray(initialDataProp)) {
        initialDataProp.forEach((item: any, index: number) => {
          Object.keys(item).forEach((subKey) => {
            let value = item[subKey];
            if (
              typeof value === "string" &&
              /^\d{2}-\d{2}-\d{4}$/.test(value)
            ) {
              const [day, month, year] = value.split("-");
              value = `${year}-${month}-${day}`;
            }

            // Safely assign the value, create the array and index if it doesn't exist yet
            if (!flattenedData[key]) {
              flattenedData[key] = [];
            }
            if (!flattenedData[key][index]) {
              flattenedData[key][index] = {};
            }

            flattenedData[key][index][subKey] = value;
          });
        });
      }
      // Normal prop
      else {
        let value = initialDataProp;
        if (typeof value === "string" && /^\d{2}-\d{2}-\d{4}$/.test(value)) {
          const [day, month, year] = value.split("-");
          value = `${year}-${month}-${day}`;
        }

        flattenedData[key] = value;
      }
    });
    return flattenedData;
  }
};

// db - elements to page test
type SchemaElement = {
  type?: string;
  name?: string;
  elements?: SchemaElement[];
  questions?: SchemaElement[];
  rows?: (SchemaElement | string)[];
  cells?: { [key: string]: SchemaElement[] };
  items?: SchemaElement[];
  [key: string]: any;
};

type PageSchema = {
  name: string;
  title: string;
  description?: string;
  elements: SchemaElement[];
  visibleIf?: string;
};

export const extractQuestionsFromSchema = (
  schemas: PageSchema[]
): { page: string; questions: SchemaElement[] }[] => {
  const isQuestion = (element: SchemaElement): boolean => {
    // Heuristically determine a question (has name and type but not a container)
    return (
      !!element.name &&
      !!element.type &&
      !element.elements &&
      !element.questions &&
      !element.items &&
      !element.rows &&
      !element.cells
    );
  };

  const extractFromElement = (element: SchemaElement): SchemaElement[] => {
    let collected: SchemaElement[] = [];

    if (isQuestion(element)) {
      collected.push(element);
    }

    if (Array.isArray(element.elements)) {
      for (const el of element.elements) {
        collected.push(...extractFromElement(el));
      }
    }

    if (Array.isArray(element.questions)) {
      for (const q of element.questions) {
        collected.push(...extractFromElement(q));
      }
    }

    if (Array.isArray(element.items)) {
      for (const item of element.items) {
        collected.push(...extractFromElement(item));
      }
    }

    if (Array.isArray(element.rows)) {
      for (const row of element.rows) {
        if (Array.isArray(row)) {
          for (const r of row) {
            if (typeof r === "object") {
              collected.push(...extractFromElement(r));
            }
          }
        } else if (typeof row === "object") {
          collected.push(...extractFromElement(row));
        }
      }
    }

    if (element.cells) {
      for (const key in element.cells) {
        const cellGroup = element.cells[key];
        for (const cell of cellGroup) {
          collected.push(...extractFromElement(cell));
        }
      }
    }

    return collected;
  };

  return schemas.map((page) => ({
    page: page.name,
    questions: page.elements.flatMap(extractFromElement),
  }));
}

function createWaistTooltipContent() {
  const container = document.createElement("div");

  // ===== Header =====
  const h4 = document.createElement("h4");
  h4.textContent = "Waist size guide";
  Object.assign(h4.style, trayStyles.header);
  container.appendChild(h4);

  // Intro paragraph
  const p1 = document.createElement("p");
  p1.textContent =
    "These are rough guides only – different brands can vary by a few cm/inches.";
  container.appendChild(p1);

  // ===== Shared table styles =====
  const tableStyle: Partial<CSSStyleDeclaration> = {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "0.5rem",
    marginBottom: "1rem",
    fontSize: "0.9rem",
    color: "#3F4254",
  };

  const thStyle: Partial<CSSStyleDeclaration> = {
    padding: "6px 8px",
    borderBottom: "1px solid #E4E6EF",
    textAlign: "left",
    fontWeight: "600",
    background: "#F3F6F9",
    color: "#181C32",
    whiteSpace: "nowrap",
  };

  const tdStyle: Partial<CSSStyleDeclaration> = {
    padding: "6px 8px",
    borderBottom: "1px solid #E4E6EF",
    verticalAlign: "top",
  };

  const rowEvenStyle: Partial<CSSStyleDeclaration> = {
    background: "#FFFFFF",
  };

  const rowOddStyle: Partial<CSSStyleDeclaration> = {
    background: "#F3F6F9",
  };

  // =====================================================
  // WOMEN – UK dress size → approx waist (table)
  // =====================================================

  const h5Women = document.createElement("h5");
  h5Women.textContent = "Women – UK dress size → approx. waist";
  Object.assign(h5Women.style, trayStyles.sectionTitle);
  container.appendChild(h5Women);

  const womenTable = document.createElement("table");
  Object.assign(womenTable.style, tableStyle);

  const womenThead = document.createElement("thead");
  const womenHeadRow = document.createElement("tr");

  const womenHeadCells = ["UK size", "Waist (inches)", "Waist (cm, approx.)"];
  womenHeadCells.forEach((text) => {
    const th = document.createElement("th");
    th.textContent = text;
    Object.assign(th.style, thStyle);
    womenHeadRow.appendChild(th);
  });

  womenThead.appendChild(womenHeadRow);
  womenTable.appendChild(womenThead);

  const womenTbody = document.createElement("tbody");

  type WomenRow = { size: string; inches: string; cm: string };

  const womenSizes: WomenRow[] = [
    { size: "6", inches: '24–25"', cm: "≈ 62" },
    { size: "8", inches: '25–26"', cm: "≈ 65" },
    { size: "10", inches: '27–28"', cm: "≈ 69" },
    { size: "12", inches: '29–30"', cm: "≈ 74" },
    { size: "14", inches: '31–32"', cm: "≈ 80" },
    { size: "16", inches: '33–34"', cm: "≈ 86" },
    { size: "18", inches: '35–36"', cm: "≈ 91" },
    { size: "20", inches: '38–40"', cm: "≈ 100" },
    { size: "22", inches: '41–42"', cm: "≈ 107" },
    { size: "24", inches: '43–44"', cm: "≈ 112" },
    { size: "26", inches: '45–46"', cm: "≈ 117" },
    { size: "28", inches: '47–48"', cm: "≈ 122" },
    { size: "30", inches: '49–50"', cm: "≈ 127" },
    { size: "32", inches: '51–52"', cm: "≈ 132" },
  ];

  womenSizes.forEach((row, index) => {
    const tr = document.createElement("tr");
    Object.assign(
      tr.style,
      index % 2 === 0 ? rowEvenStyle : rowOddStyle
    );

    const tdSize = document.createElement("td");
    tdSize.textContent = row.size;
    Object.assign(tdSize.style, tdStyle);

    const tdInches = document.createElement("td");
    tdInches.textContent = row.inches;
    Object.assign(tdInches.style, tdStyle);

    const tdCm = document.createElement("td");
    tdCm.textContent = row.cm;
    Object.assign(tdCm.style, tdStyle);

    tr.appendChild(tdSize);
    tr.appendChild(tdInches);
    tr.appendChild(tdCm);

    womenTbody.appendChild(tr);
  });

  womenTable.appendChild(womenTbody);
  container.appendChild(womenTable);

  // =====================================================
  // MEN – jeans size (inches) → waist (cm)
  // =====================================================

  const h5Men = document.createElement("h5");
  h5Men.textContent = "Men – UK jeans size (waist in inches)";
  Object.assign(h5Men.style, trayStyles.sectionTitle);
  container.appendChild(h5Men);

  const menTable = document.createElement("table");
  Object.assign(menTable.style, tableStyle);

  const menThead = document.createElement("thead");
  const menHeadRow = document.createElement("tr");

  const menHeadCells = ["Jeans size (inches)", "Waist (cm, approx.)"];
  menHeadCells.forEach((text) => {
    const th = document.createElement("th");
    th.textContent = text;
    Object.assign(th.style, thStyle);
    menHeadRow.appendChild(th);
  });

  menThead.appendChild(menHeadRow);
  menTable.appendChild(menThead);

  const menTbody = document.createElement("tbody");

  type MenRow = { inches: string; cm: string };

  const menSizes: MenRow[] = [
    { inches: '28"', cm: "≈ 71" },
    { inches: '30"', cm: "≈ 76" },
    { inches: '32"', cm: "≈ 81" },
    { inches: '34"', cm: "≈ 86" },
    { inches: '36"', cm: "≈ 91" },
    { inches: '38"', cm: "≈ 97" },
    { inches: '40"', cm: "≈ 102" },
    { inches: '42"', cm: "≈ 107" },
    { inches: '44"', cm: "≈ 112" },
    { inches: '46"', cm: "≈ 117" },
    { inches: '48"', cm: "≈ 122" },
    { inches: '50"', cm: "≈ 127" },
    { inches: '52"', cm: "≈ 132" },
    { inches: '54"', cm: "≈ 137" },
    { inches: '56"', cm: "≈ 142" },
    { inches: '58"', cm: "≈ 147" },
    { inches: '60"', cm: "≈ 152" },
    { inches: '62"', cm: "≈ 157" },
    { inches: '64"', cm: "≈ 163" },
    { inches: '66"', cm: "≈ 168" },
    { inches: '68"', cm: "≈ 173" },
    { inches: '70"', cm: "≈ 178" },
  ];

  menSizes.forEach((row, index) => {
    const tr = document.createElement("tr");
    Object.assign(
      tr.style,
      index % 2 === 0 ? rowEvenStyle : rowOddStyle
    );

    const tdInches = document.createElement("td");
    tdInches.textContent = row.inches;
    Object.assign(tdInches.style, tdStyle);

    const tdCm = document.createElement("td");
    tdCm.textContent = row.cm;
    Object.assign(tdCm.style, tdStyle);

    tr.appendChild(tdInches);
    tr.appendChild(tdCm);

    menTbody.appendChild(tr);
  });

  menTable.appendChild(menTbody);
  container.appendChild(menTable);

  // Final paragraph
  const p2 = document.createElement("p");
  p2.textContent =
    "If you're unsure of your waist, you can use the closest dress or jeans size above to estimate it.";
  container.appendChild(p2);

  return container;
}

const applyResponsiveTrayWidth = (tray: HTMLDivElement) => {
  const vw = window.innerWidth;

  // Mobile: full width
  if (vw < 576) {
    tray.style.width = "100%";
    tray.style.maxWidth = "100%";
  }
  // Tablet: wider but not full width
  else if (vw < 992) {
    tray.style.width = "420px";
    tray.style.maxWidth = "80%";
  }
  // Desktop: narrower tray on the right
  else {
    tray.style.width = "360px";
    tray.style.maxWidth = "30%";
  }
};

const trayStyles = {
  tray: {
    position: "fixed",
    top: "0",
    right: "0",
    height: "100%",
    background: "#ffffff",
    borderLeft: "1px solid #E4E6EF",
    boxShadow: "-8px 0 24px rgba(15, 23, 42, 0.15)",
    zIndex: "9999",
    padding: "24px 28px",
    fontFamily: '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: "0.95rem",
    lineHeight: "1.6",
    color: "#222222",
    overflowY: "auto",
    transform: "translateX(100%)",
    transition: "transform 0.25s ease-out",
  },

  closeButton: {
    position: "absolute",
    top: "14px",
    right: "16px",
    background: "transparent",
    border: "none",
    fontSize: "20px",
    cursor: "pointer",
    color: "#7E8299",
  },

  header: {
    marginTop: "0",
    marginBottom: "0.75rem",
    fontSize: "2rem",
    fontWeight: 600,
    color: "#181C32",
  },

  sectionTitle: {
    marginTop: "1.25rem",
    marginBottom: "0.35rem",
    fontSize: "1.25rem",
    fontWeight: 600,
    textTransform: "none",
    color: "#181C32",
  },

  list: {
    margin: "0.25rem 0 1.5rem 1.25rem",
    padding: "0",
  },
};


